/**
 * ShieldWall — Behavioral Biometrics Collector
 *
 * Tracks mouse movement, keystroke dynamics, scroll behavior,
 * touch events, and error/correction patterns to build a
 * behavioral trust score.
 *
 * A11Y-aware: grants baseline credit for assistive technology users
 * (touch-only, reduced-motion, keyboard-only navigation).
 *
 * References:
 *   - Research §2.1: High-signal interaction features
 *   - Research §2.2: Modern bot evasion countermeasures
 *   - Research §2.3: WCAG 2.2 SC 3.3.8 accessible authentication
 *
 * @module useBehaviorCollector
 */

import { useEffect, useRef, useCallback } from "react";

export interface BehaviorStats {
  mousePoints: number;
  keyIntervals: number;
  scrolls: number;
  scrollDirChanges: number;
  clicks: number;
  touches: number;
  backspaces: number;
  totalKeys: number;
  sessionSec: number;
  isA11y: boolean;
  sensorSamples: number;
  /** Number of instantaneous large-distance cursor jumps detected (click-farm signal). */
  teleportEvents: number;
}

export interface SensorSample {
  t: number;
  ax?: number;
  ay?: number;
  az?: number;
  rx?: number;
  ry?: number;
  rz?: number;
  ox?: number;
  oy?: number;
  oz?: number;
}

export interface BehaviorResult {
  score: number;
  signals: string[];
  stats: BehaviorStats;
  sensorSamples: SensorSample[];
  /**
   * FNV-1a commitment binding behavior data to the challenge nonce.
   * Null when no nonce was provided (backward compat).
   */
  nonceBinding: string | null;
  /**
   * The exact inputs hashed into `nonceBinding`, sent to the server so it can
   * recompute and verify the hash (SW-113). Null when no nonce was provided.
   */
  nonceBindingMeta: NonceBindingMeta | null;
}

/** Inputs the client hashes into `nonceBinding`; the server recomputes from these. */
export interface NonceBindingMeta {
  mousePoints: number;
  sensorSamples: number;
  clicks: number;
  firstMouseT: number;
  lastMouseT: number;
  firstSensorT: number;
  lastSensorT: number;
}

interface CollectorData {
  mousePositions: Array<{ x: number; y: number; t: number }>;
  mouseSpeeds: number[];
  clicks: Array<{ x: number; y: number; t: number }>;
  keyIntervals: number[];
  scrollEvents: number[];
  touchEvents: Array<{ touches: number; t: number }>;
  startTime: number;
  lastKeyTime: number;
  lastMouseTime: number;
  tabSwitches: number;
  backspaces: number;
  totalKeys: number;
  hasReducedMotion: boolean;
  scrollDirectionChanges: number;
  lastScrollDir: number;
  _lastScrollY: number;
  sensorSamples: SensorSample[];
  lastSensorTime: number;
}

const MAX_BEHAVIOR_SCORE = 60;
const SENSOR_THROTTLE_MS = 50;
const SENSOR_SAMPLE_CAP = 120;

/**
 * Cursor teleportation detection thresholds.
 * Distance > TELEPORT_DISTANCE_PX in < TELEPORT_TIME_MS indicates a remote-desktop
 * handoff or click-farm iframe injection. Real mice cannot move 400px in under 5ms.
 */
const TELEPORT_DISTANCE_PX = 400;
const TELEPORT_TIME_MS = 5;
const TELEPORT_PENALTY_PER = 8;
const TELEPORT_PENALTY_CAP = 24;

/**
 * FNV-1a 32-bit hash — lightweight, synchronous hash for nonce binding.
 * This is NOT cryptographic; it's a fast commitment that the server
 * can recompute from the submitted data + nonce. The real security
 * comes from the HMAC-signed nonce being single-use in Redis.
 */
function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function useBehaviorCollector() {
  const data = useRef<CollectorData>({
    mousePositions: [],
    mouseSpeeds: [],
    clicks: [],
    keyIntervals: [],
    scrollEvents: [],
    touchEvents: [],
    startTime: Date.now(),
    lastKeyTime: 0,
    lastMouseTime: 0,
    tabSwitches: 0,
    backspaces: 0,
    totalKeys: 0,
    hasReducedMotion: false,
    scrollDirectionChanges: 0,
    lastScrollDir: 0,
    _lastScrollY: 0,
    sensorSamples: [],
    lastSensorTime: 0,
  });

  useEffect(() => {
    const d = data.current;

    // Check for reduced motion preference (A11Y signal)
    d.hasReducedMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;

    const handlers = {
      mousemove: (e: MouseEvent) => {
        const now = performance.now();
        d.mousePositions.push({ x: e.clientX, y: e.clientY, t: now });

        if (d.lastMouseTime > 0 && d.mousePositions.length > 1) {
          const prev = d.mousePositions[d.mousePositions.length - 2]!;
          const dt = now - d.lastMouseTime;
          if (dt > 0) {
            d.mouseSpeeds.push(
              Math.hypot(e.clientX - prev.x, e.clientY - prev.y) / dt
            );
          }
        }
        d.lastMouseTime = now;

        // Cap stored events to prevent memory bloat
        if (d.mousePositions.length > 500) d.mousePositions.shift();
        if (d.mouseSpeeds.length > 500) d.mouseSpeeds.shift();
      },

      click: (e: MouseEvent) => {
        d.clicks.push({ x: e.clientX, y: e.clientY, t: performance.now() });
      },

      keydown: (e: KeyboardEvent) => {
        const now = performance.now();
        d.totalKeys++;

        // Track corrections (humans make mistakes, bots rarely do)
        if (e.key === "Backspace" || e.key === "Delete") {
          d.backspaces++;
        }

        if (d.lastKeyTime > 0) {
          d.keyIntervals.push(now - d.lastKeyTime);
        }
        d.lastKeyTime = now;

        if (d.keyIntervals.length > 200) d.keyIntervals.shift();
      },

      scroll: () => {
        const y = window.scrollY;
        const dir = y > d._lastScrollY ? 1 : -1;

        // Track direction changes (humans scroll back and forth)
        if (d.lastScrollDir !== 0 && dir !== d.lastScrollDir) {
          d.scrollDirectionChanges++;
        }
        d.lastScrollDir = dir;
        d._lastScrollY = y;

        d.scrollEvents.push(performance.now());
        if (d.scrollEvents.length > 100) d.scrollEvents.shift();
      },

      touchstart: (e: TouchEvent) => {
        d.touchEvents.push({
          touches: e.touches.length,
          t: performance.now(),
        });
      },

      visibilitychange: () => {
        if (document.hidden) d.tabSwitches++;
      },
    };

    const opts: AddEventListenerOptions = { passive: true };

    window.addEventListener("mousemove", handlers.mousemove, opts);
    window.addEventListener("click", handlers.click, opts);
    window.addEventListener("keydown", handlers.keydown, opts);
    window.addEventListener("scroll", handlers.scroll, opts);
    window.addEventListener("touchstart", handlers.touchstart, opts);
    document.addEventListener("visibilitychange", handlers.visibilitychange);

    const pushSample = (sample: SensorSample) => {
      const now = performance.now();
      if (now - d.lastSensorTime < SENSOR_THROTTLE_MS) return;
      d.lastSensorTime = now;
      // Clamp t to non-negative — guards against environments where Date.now()
      // disagrees with d.startTime (browser extension stubs, clock skew, etc).
      if (!Number.isFinite(sample.t) || sample.t < 0) sample.t = 0;
      d.sensorSamples.push(sample);
      if (d.sensorSamples.length > SENSOR_SAMPLE_CAP) d.sensorSamples.shift();
    };

    const onMotion = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity ?? e.acceleration;
      const r = e.rotationRate;
      pushSample({
        t: Date.now() - d.startTime,
        ax: a?.x ?? undefined,
        ay: a?.y ?? undefined,
        az: a?.z ?? undefined,
        rx: r?.alpha ?? undefined,
        ry: r?.beta ?? undefined,
        rz: r?.gamma ?? undefined,
      });
    };

    const onOrientation = (e: DeviceOrientationEvent) => {
      pushSample({
        t: Date.now() - d.startTime,
        ox: e.alpha ?? undefined,
        oy: e.beta ?? undefined,
        oz: e.gamma ?? undefined,
      });
    };

    let sensorsAttached = false;
    const attachSensorListeners = () => {
      if (sensorsAttached) return;
      sensorsAttached = true;
      window.addEventListener("devicemotion", onMotion, opts);
      window.addEventListener("deviceorientation", onOrientation, opts);
    };

    // Skip sensor collection for reduced-motion users — baseline credit covers them.
    if (!d.hasReducedMotion && typeof window !== "undefined") {
      const DME = (window as unknown as { DeviceMotionEvent?: { requestPermission?: () => Promise<string> } })
        .DeviceMotionEvent;
      if (typeof DME?.requestPermission === "function") {
        // iOS Safari: permission must follow a user gesture. Request on first pointer/touch,
        // never block verification — feature-detect and fall through on denial/error.
        const gestureOnce = () => {
          window.removeEventListener("touchstart", gestureOnce);
          window.removeEventListener("click", gestureOnce);
          DME.requestPermission!()
            .then((state) => {
              if (state === "granted") attachSensorListeners();
            })
            .catch(() => {
              /* permission denied or no secure context — ignore */
            });
        };
        window.addEventListener("touchstart", gestureOnce, { once: true, passive: true });
        window.addEventListener("click", gestureOnce, { once: true, passive: true });
      } else {
        attachSensorListeners();
      }
    }

    return () => {
      window.removeEventListener("mousemove", handlers.mousemove);
      window.removeEventListener("click", handlers.click);
      window.removeEventListener("keydown", handlers.keydown);
      window.removeEventListener("scroll", handlers.scroll);
      window.removeEventListener("touchstart", handlers.touchstart);
      document.removeEventListener(
        "visibilitychange",
        handlers.visibilitychange
      );
      if (sensorsAttached) {
        window.removeEventListener("devicemotion", onMotion);
        window.removeEventListener("deviceorientation", onOrientation);
      }
    };
  }, []);

  const getScore = useCallback((nonce?: string): BehaviorResult => {
    const d = data.current;
    let score = 0;
    const signals: string[] = [];

    const isTouchOnly =
      d.touchEvents.length > 0 && d.mousePositions.length < 5;
    const isA11y = d.hasReducedMotion || isTouchOnly;

    // ── Mouse Movement Analysis ──
    if (d.mousePositions.length > 8) {
      score += 10;
      signals.push("mouse_movement");

      // Path curvature: humans have curved, non-optimal trajectories
      // Bots move in straight lines or piecewise linear segments
      const angles: number[] = [];
      for (let i = 2; i < Math.min(d.mousePositions.length, 60); i++) {
        const [p0, p1, p2] = [
          d.mousePositions[i - 2]!,
          d.mousePositions[i - 1]!,
          d.mousePositions[i]!,
        ];
        const a1 = Math.atan2(p1.y - p0.y, p1.x - p0.x);
        const a2 = Math.atan2(p2.y - p1.y, p2.x - p1.x);
        angles.push(Math.abs(a2 - a1));
      }
      if (
        angles.length > 0 &&
        angles.reduce((a, b) => a + b, 0) / angles.length > 0.04
      ) {
        score += 7;
        signals.push("curved_path");
      }

      // Speed coefficient of variation (humans: 0.2-2.0 CV)
      if (d.mouseSpeeds.length > 4) {
        const mean =
          d.mouseSpeeds.reduce((a, b) => a + b, 0) / d.mouseSpeeds.length;
        const std = Math.sqrt(
          d.mouseSpeeds.reduce((a, b) => a + (b - mean) ** 2, 0) /
            d.mouseSpeeds.length
        );
        const cv = mean > 0 ? std / mean : 0;
        if (cv > 0.2 && cv < 2.0) {
          score += 7;
          signals.push(`speed_cv:${cv.toFixed(2)}`);
        }
      }

      // Pre-click mouse movement (humans move mouse before clicking)
      if (d.clicks.length > 0 && d.mousePositions.length > 5) {
        const firstClick = d.clicks[0]!;
        const preClickMoves = d.mousePositions.filter(
          (p) => p.t < firstClick.t
        );
        if (preClickMoves.length > 3) {
          score += 5;
          signals.push("pre_click_movement");
        }
      }
    } else if (isA11y) {
      // A11Y users get baseline credit — don't penalize for missing mouse data
      score += 15;
      signals.push("a11y_baseline_credit");
    }

    // ── Keystroke Dynamics ──
    if (d.keyIntervals.length > 2) {
      score += 6;
      signals.push("keyboard_input");

      const mean =
        d.keyIntervals.reduce((a, b) => a + b, 0) / d.keyIntervals.length;
      const std = Math.sqrt(
        d.keyIntervals.reduce((a, b) => a + (b - mean) ** 2, 0) /
          d.keyIntervals.length
      );
      const cv = mean > 0 ? std / mean : 0;

      // Humans have 0.12-2.0 CV in keystroke timing
      // Bots are near-zero (uniform) or erratic
      if (cv > 0.12 && cv < 2.0) {
        score += 6;
        signals.push("human_typing_rhythm");
      }
    }

    // ── Error/Correction Patterns ──
    // Humans make mistakes and correct them. Bots rarely backspace.
    if (d.totalKeys > 5 && d.backspaces > 0) {
      const errorRate = d.backspaces / d.totalKeys;
      if (errorRate > 0.02 && errorRate < 0.4) {
        score += 4;
        signals.push("correction_patterns");
      }
    }

    // ── Scroll Behavior ──
    if (d.scrollEvents.length > 1) {
      score += 3;
      signals.push("scroll");
    }
    if (d.scrollDirectionChanges > 0) {
      score += 3;
      signals.push("scroll_direction_changes");
    }

    // ── Interaction Signals ──
    if (d.clicks.length >= 1) {
      score += 3;
      signals.push("click");
    }
    if (d.touchEvents.length > 0) {
      score += 6;
      signals.push("touch");
    }

    // ── Cursor Teleportation Detection (Click-Farm Defense) ──
    // Remote-desktop handoff or solver-farm iframe injection causes
    // instantaneous cursor jumps that no real mouse can produce.
    let teleportEvents = 0;
    for (let i = 1; i < d.mousePositions.length; i++) {
      const prev = d.mousePositions[i - 1]!;
      const cur = d.mousePositions[i]!;
      const dt = cur.t - prev.t;
      const dist = Math.hypot(cur.x - prev.x, cur.y - prev.y);
      if (dist > TELEPORT_DISTANCE_PX && dt < TELEPORT_TIME_MS) {
        teleportEvents++;
      }
    }
    if (teleportEvents > 0) {
      const penalty = Math.min(teleportEvents * TELEPORT_PENALTY_PER, TELEPORT_PENALTY_CAP);
      score -= penalty;
      signals.push(`cursor_teleportation:${teleportEvents}`);
    }

    // ── Session Duration ──
    const sessionSec = (Date.now() - d.startTime) / 1000;
    if (sessionSec > 1.5 && sessionSec < 600) {
      score += 3;
      signals.push("session_duration");
    }

    return {
      score: Math.min(score, MAX_BEHAVIOR_SCORE),
      signals,
      stats: {
        mousePoints: d.mousePositions.length,
        keyIntervals: d.keyIntervals.length,
        scrolls: d.scrollEvents.length,
        scrollDirChanges: d.scrollDirectionChanges,
        clicks: d.clicks.length,
        touches: d.touchEvents.length,
        backspaces: d.backspaces,
        totalKeys: d.totalKeys,
        sessionSec: Math.round(sessionSec),
        isA11y,
        sensorSamples: d.sensorSamples.length,
        teleportEvents,
      },
      sensorSamples: d.sensorSamples.slice(),
      nonceBinding: nonce ? computeNonceBinding(nonce, d).hash : null,
      nonceBindingMeta: nonce ? computeNonceBinding(nonce, d).meta : null,
    };
  }, []);

  return { getScore };
}

/**
 * Compute a deterministic binding hash over the challenge nonce + behavioral
 * data shape. The server recomputes this from the submitted nonce + sample
 * counts + boundary timestamps to verify the behavioral data was genuinely
 * collected during this challenge session — not replayed from a recording.
 *
 * Binding inputs (must match server-side recomputation):
 *   nonce + mousePoints + sensorSamples.length + clicks.length
 *   + first mouse timestamp + last mouse timestamp
 *   + first sensor timestamp + last sensor timestamp
 */
function computeNonceBinding(
  nonce: string,
  d: CollectorData
): { hash: string; meta: NonceBindingMeta } {
  const meta: NonceBindingMeta = {
    mousePoints: d.mousePositions.length,
    sensorSamples: d.sensorSamples.length,
    clicks: d.clicks.length,
    firstMouseT: d.mousePositions.length > 0 ? Math.round(d.mousePositions[0]!.t) : 0,
    lastMouseT:
      d.mousePositions.length > 0 ? Math.round(d.mousePositions[d.mousePositions.length - 1]!.t) : 0,
    firstSensorT: d.sensorSamples.length > 0 ? d.sensorSamples[0]!.t : 0,
    lastSensorT: d.sensorSamples.length > 0 ? d.sensorSamples[d.sensorSamples.length - 1]!.t : 0,
  };

  // Join order MUST match the server's ScoringService.VerifyNonceBinding.
  const bindingInput = [
    nonce,
    meta.mousePoints,
    meta.sensorSamples,
    meta.clicks,
    meta.firstMouseT,
    meta.lastMouseT,
    meta.firstSensorT,
    meta.lastSensorT,
  ].join(":");

  return { hash: fnv1a(bindingInput), meta };
}
